import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { editClient } from "../../../slices/clientesSlice";
import DefaultProfilePicture from "../../../assets/clientIcon.png";

export default function ModalEditCliente({
  isEditModal,
  setIsEditModal,
  successEditClient,
}) {
  const dispatch = useDispatch();
  const { client } = useSelector((state) => state.clientes);

  const [imageUrl, setImageUrl] = useState(
    client?.profileImage || DefaultProfilePicture
  );
  const [selectedProfilePictureFile, setSelectedProfilePictureFile] =
    useState(null);
  const fileInputRef = useRef(null);

  const [formDataClient, setFormData] = useState({
    name: client?.name || "",
    email: client?.email || "",
    phone: client?.phone || "N/A",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formDataClient,
      [name]: value,
    });
  };

  const handleProfilePicture = (file) => {
    if (file) {
      setSelectedProfilePictureFile(file);
      const objectUrl = URL.createObjectURL(file);
      setImageUrl(objectUrl);
    }
  };

  const handleRemoveProfilePicture = () => {
    setSelectedProfilePictureFile(null);
    setImageUrl(DefaultProfilePicture);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Limpiar el input de archivo
    }
  };

  const handleSubmit = async (id) => {
    const formData = new FormData();

    if (selectedProfilePictureFile) {
      formData.append("imageProfile",
        selectedProfilePictureFile
      );
    }

    let hasChangesValid = false;
    const updatedUser = {};

    if (formDataClient.name !== client.name) {
      updatedUser.name = formDataClient.name;
      hasChangesValid = true;
    }
    if (formDataClient.email !== client.email) {
      updatedUser.email = formDataClient.email;
      hasChangesValid = true;
    }
    if (formDataClient.phone !== client.phone) {
      updatedUser.phone = formDataClient.phone;
      hasChangesValid = true;
    }

    if (hasChangesValid || selectedProfilePictureFile) {
      formData.append("client", JSON.stringify(updatedUser));
      await dispatch(editClient({ id, formData }));
      successEditClient();
    }

    setIsEditModal(false);
  };

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        email: client.email,
        phone: client.phone,
      });
      setImageUrl(client.profileImage || DefaultProfilePicture);
    }
  }, [client]);

  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl !== DefaultProfilePicture) {
        URL.revokeObjectURL(imageUrl); // Liberar la URL del objeto cuando el componente se desmonta
      }
    };
  }, [imageUrl]);

  return (
    <AnimatePresence>
      {isEditModal && (
        <motion.div
          key="modal-background"
          onClick={() => setIsEditModal(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            key="modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-background font-poppina p-8 rounded-lg shadow-lg max-w-2xl w-full mx-4"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-text">
                Información del Cliente
              </h3>
              <button
                onClick={() => setIsEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex flex-col md:flex-row gap-8">
              
              <div className="flex relative flex-col items-center text-center md:text-left">
                <div className="absolute top-2 right-0">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleProfilePicture(e.target.files[0])}
                    style={{ display: "none" }}
                    accept="image/*"
                  />
                </div>
                <div
                  className="relative rounded-full w-40 h-40 mb-4 overflow-hidden group"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ cursor: "pointer" }}
                >
                  <img
                    className="w-full h-full object-cover"
                    src={imageUrl}
                    alt={formDataClient.name}
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm">Cambiar foto</span>
                  </div>
                </div>
                <h1 className="text-2xl font-semibold text-text">
                  {formDataClient.name}
                </h1>
              </div>

              
              <div className="flex-1">
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-medium text-text">
                      Información de Contacto
                    </h2>
                    <input
                      type="text"
                      name="name"
                      value={formDataClient.name}
                      onChange={handleChange}
                      className="w-full p-2 bg-background border border-border text-text rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nombre"
                    />
                    <input
                      type="email"
                      name="email"
                      value={formDataClient.email}
                      onChange={handleChange}
                      className="w-full  p-2 border bg-background  border-border text-text rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                      placeholder="Correo electrónico"
                    />
                    <input
                      type="text"
                      name="phone"
                      value={formDataClient.phone}
                      onChange={handleChange}
                      className="w-full p-2 bg-background border border-border text-text rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                      placeholder="Teléfono"
                    />
                  </div>
                </div>
              </div>
            </div>

          
            <div className="flex justify-end mt-4">
              <button
                onClick={() => handleSubmit(client._id)}
                className="bg-blue-500 px-4 py-2 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Guardar Cambios
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
